/**
 * WorkflowHomePage - Listagem dos processos do módulo Workflow 2.0
 *
 * Mostra os processos configurados pelo tenant e abre o construtor visual.
 * Leitura e escrita exclusivamente por RPCs seguras (useWorkflowDesigner).
 *
 * @version 2.5.0
 * RELEASE: 2026-10-06 v2.5.0 - Botão "Novo processo" no tamanho "xs" (mesmo
 *          tamanho do botão "Novo Fluxo" da tela Fluxos).
 * RELEASE: 2026-10-06 v2.4.0 - Alternador Cards/Lista (padrão da tela Fluxos, preferência
 *          salva no navegador) e menu de ações compartilhado entre os dois modos.
 * RELEASE: 2026-10-06 v2.3.0 - Workflow ganha submenus: Pendências e Erros do processo
 *          saem desta página (páginas próprias); topo ganha contadores de
 *          pendências e erros que levam à página correspondente já filtrada.
 * RELEASE: 2026-09-13 v2.2.0 - Entidades Retenção e Jurídico só são oferecidas
 *          quando o submódulo correspondente está ligado para a empresa.
 * RELEASE: 2026-08-31 v2.1.0 - Ações de ativar/desativar e excluir processo
 *          (exclusão bloqueada quando existem execuções em andamento) +
 *          filtro de processos desativados e contadores de execução.
 * RELEASE: 2026-08-30 v2.0.0 - Listagem real do motor v2 + criação de processo
 * RELEASE: 2026-08-30 v1.0.0 - Página provisória (Workflow 2.0 Fase 0)
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Lock,
  GitBranch,
  LayoutGrid,
  List,
  Loader2,
  MoreVertical,
  Pencil,
  Play,
  Plus,
  Power,
  Settings,
  Trash2,
  Workflow as WorkflowIcon,
  X,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useWorkflowOverview } from '@/hooks/useWorkflowDesigner';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { usePermissions } from '@/hooks/usePermissions';
// v2.2.0: flags dos submódulos de tarefa (Retenção/Jurídico)
import { useTaskSubmodulesEnabled } from '@/hooks/useTasksModuleSettings';
import { WorkflowBottlenecksPanel } from '@/components/workflow/WorkflowBottlenecksPanel';
import { WorkflowSettingsDialog } from '@/components/workflow/WorkflowSettingsDialog';
import { WorkflowProcessFields } from '@/components/workflow/WorkflowProcessFields';
import { WorkflowDialogShell } from '@/components/workflow/WorkflowDialogShell';
// Observabilidade: indicadores e log de execuções de todos os processos
import { WorkflowExecutionsDialog } from '@/components/workflow/WorkflowExecutionsDialog';
import { useWorkflowExecutionLog } from '@/hooks/useWorkflowExecutions';
import { KpiCard } from '@/components/layout/list-page';
import { cn } from '@/lib/utils';
import { WorkflowProcessListRow } from '@/components/workflow/WorkflowProcessListRow';
// v2.3.0: contadores de pendências/erros (topo da página)
import { useWorkflowOverviewKpis } from '@/hooks/useWorkflowOverviewKpis';


/** Item da listagem (subset do payload da RPC `get_workflow_overview`) */
type DefinicaoLista = NonNullable<ReturnType<typeof useWorkflowOverview>['overview']>['definitions'][number];

export default function WorkflowHomePage() {
  const navigate = useNavigate();
  const { overview, isLoading, upsertDefinition, setStatus, deleteDefinition } = useWorkflowOverview();
  const { workspaces } = useCrmWorkspacesHierarchy();

  // Fase 6: criação de processo controlada por permissão granular
  const { canCreate, canEdit, canDelete } = usePermissions();
  const podeCriar = canCreate('workflow_processes');
  const podeEditar = canEdit('workflow_processes');
  const podeExcluir = canDelete('workflow_processes');

  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [entidade, setEntidade] = useState('crm_deal');
  // Campos gerais equiparados ao modal de configurações
  const [enforcement, setEnforcement] = useState('allow');
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([]);

  // Processos desativados ficam ocultos por padrão para não poluir a tela
  const [mostrarDesativados, setMostrarDesativados] = useState(false);

  // v2.4.0: modo de visualização (cards/lista) com chave própria no navegador
  const [modoVisual, setModoVisual] = useState<'cards' | 'list'>(() =>
    localStorage.getItem('workflow_processes_viewMode') === 'list' ? 'list' : 'cards',
  );
  const trocarModo = (m: 'cards' | 'list') => {
    setModoVisual(m);
    localStorage.setItem('workflow_processes_viewMode', m);
  };

  // ---------------------------------------------------------------------------
  // Observabilidade: indicadores gerais + modal de execuções
  // ---------------------------------------------------------------------------
  const { kpis: kpisExecucoes } = useWorkflowExecutionLog({ pageSize: 1 });
  const [execucoesAbertas, setExecucoesAbertas] = useState(false);
  // v2.3.0: pendências e erros abertos (cada cartão leva ao submenu filtrado)
  const { kpis: kpisGerais } = useWorkflowOverviewKpis();
  const [execFiltroStatus, setExecFiltroStatus] = useState('todos');
  const [execFiltroPeriodo, setExecFiltroPeriodo] = useState('todos');

  /** Abre o painel de execuções já filtrado pelo card clicado */
  const abrirExecucoes = (status: string, periodo: string = 'todos') => {
    setExecFiltroStatus(status);
    setExecFiltroPeriodo(periodo);
    setExecucoesAbertas(true);
  };

  // Estado do diálogo de exclusão
  const [alvoExclusao, setAlvoExclusao] = useState<DefinicaoLista | null>(null);
  // Processo aberto no diálogo de configurações gerais
  const [alvoConfig, setAlvoConfig] = useState<DefinicaoLista | null>(null);
  const [confirmacaoNome, setConfirmacaoNome] = useState('');

  /*
   * v2.2.0 — Retenção e Jurídico só aparecem quando o submódulo correspondente
   * está ligado para a empresa. As demais entidades do catálogo seguem
   * exatamente como antes (nenhum comportamento existente foi alterado).
   */
  const { retention: retencaoLigada, legal: juridicoLigado } = useTaskSubmodulesEnabled();
  const entidadesAtivas = useMemo(
    () =>
      (overview?.entity_types ?? []).filter((e) => {
        if (!e.is_active) return false;
        if (e.code === 'retention') return retencaoLigada;
        if (e.code === 'legal') return juridicoLigado;
        return true;
      }),
    [overview?.entity_types, retencaoLigada, juridicoLigado],
  );

  const definicoes = overview?.definitions ?? [];
  const totalDesativados = definicoes.filter((d) => d.status === 'archived').length;
  const visiveis = useMemo(
    () => (mostrarDesativados ? definicoes : definicoes.filter((d) => d.status !== 'archived')),
    [definicoes, mostrarDesativados],
  );

  /** Marca/desmarca um workspace do escopo do novo processo */
  const alternarWorkspace = (id: string) =>
    setWorkspaceIds((atual) => (atual.includes(id) ? atual.filter((w) => w !== id) : [...atual, id]));

  /** Cria o processo e já abre o construtor visual */
  const criar = async () => {
    const id = await upsertDefinition.mutateAsync({
      name: nome.trim(),
      description: descricao.trim() || null,
      root_entity_type: entidade,
      default_enforcement: enforcement,
      workspace_ids: workspaceIds,
    });
    setAberto(false);
    setNome('');
    setDescricao('');
    setEnforcement('allow');
    setWorkspaceIds([]);
    navigate(`/tenant/workflow/${id}`);
  };


  /** Confirma a exclusão respeitando a necessidade de purga do histórico */
  const confirmarExclusao = async () => {
    if (!alvoExclusao) return;
    const historico = alvoExclusao.instance_count ?? 0;
    await deleteDefinition.mutateAsync({ id: alvoExclusao.id, confirmPurge: historico > 0 });
    setAlvoExclusao(null);
    setConfirmacaoNome('');
  };

  /**
   * Rótulo e tom do selo de situação, no padrão "bolinha colorida + palavra"
   * (mesmo modelo da listagem de categorias), substituindo o Badge balão.
   */
  const selo = (status: string) =>
    status === 'active'
      ? { texto: 'Ativo', tom: 'success' as const }
      : status === 'archived'
        ? { texto: 'Desativado', tom: 'neutral' as const }
        : { texto: 'Rascunho', tom: 'warning' as const };

  // Exclusão com histórico encerrado exige digitar o nome do processo
  const exigeDigitacao = (alvoExclusao?.instance_count ?? 0) > 0;
  const podeConfirmar =
    !!alvoExclusao && (!exigeDigitacao || confirmacaoNome.trim() === alvoExclusao.name.trim());

  /**
   * v2.4.0: menu de ações compartilhado entre a visualização em cards e em lista
   * (mesmas regras de permissão e de bloqueio que já existiam no card).
   */
  const menuAcoes = (d: DefinicaoLista, abertas: number) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => navigate(`/tenant/workflow/${d.id}`)}>
          <Pencil className="mr-2 h-4 w-4" /> Editar
        </DropdownMenuItem>

        {/* Configurações gerais (nome, escopo, política) sem abrir o construtor */}
        <DropdownMenuItem onSelect={() => setAlvoConfig(d)}>
          <Settings className="mr-2 h-4 w-4" /> Configurações
        </DropdownMenuItem>


        {d.status === 'active' ? (
          <DropdownMenuItem
            disabled={!podeEditar || setStatus.isPending}
            onSelect={() => setStatus.mutate({ id: d.id, status: 'archived' })}
          >
            <Power className="mr-2 h-4 w-4" /> Desativar
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={!podeEditar || setStatus.isPending || !d.active_version_id}
            title={
              d.active_version_id
                ? undefined
                : 'Publique uma versão antes de ativar o processo'
            }
            onSelect={() => setStatus.mutate({ id: d.id, status: 'active' })}
          >
            <Play className="mr-2 h-4 w-4" /> Ativar
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          disabled={!podeExcluir || abertas > 0}
          title={
            abertas > 0
              ? `${abertas} execução(ões) em andamento; desative o processo`
              : undefined
          }
          onSelect={() => {
            setConfirmacaoNome('');
            setAlvoExclusao(d);
          }}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="container mx-auto space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Workflow</h1>
          <p className="text-sm text-muted-foreground">
            Orquestre processos com etapas, gateways, esperas e ações automáticas.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {totalDesativados > 0 && (
            <div className="flex items-center gap-2">
              <Switch
                id="mostrar-desativados"
                checked={mostrarDesativados}
                onCheckedChange={setMostrarDesativados}
              />
              <Label htmlFor="mostrar-desativados" className="text-xs text-muted-foreground">
                Mostrar desativados ({totalDesativados})
              </Label>
            </div>
          )}
          {/* v2.4.0: alternador Cards/Lista (mesmo desenho da tela Fluxos) */}
          <div className="flex items-center rounded-lg border border-border bg-muted/50 p-1">
            {(['cards', 'list'] as const).map((m) => {
              const Icone = m === 'cards' ? LayoutGrid : List;
              return (
                <button
                  key={m}
                  type="button"
                  aria-label={m === 'cards' ? 'Ver em cards' : 'Ver em lista'}
                  onClick={() => trocarModo(m)}
                  className={cn(
                    'flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                    modoVisual === m ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icone className="h-4 w-4" />
                </button>
              );
            })}
          </div>
          {/* v2.5.0: mesmo tamanho do botão "Novo Fluxo" (size="xs") */}
          <Button size="xs" onClick={() => setAberto(true)} disabled={!podeCriar}>
            <Plus className="mr-1 h-4 w-4" /> Novo processo
          </Button>
        </div>
      </div>

      {/* Indicadores de execução (clicáveis: abrem o log já filtrado) */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <KpiCard
          color="purple"
          icon={Activity}
          label="Total executado"
          value={kpisExecucoes.total}
          onClick={() => abrirExecucoes('todos')}
        />
        <KpiCard
          color="green"
          icon={CheckCircle2}
          label="Sucesso"
          value={kpisExecucoes.concluidas}
          onClick={() => abrirExecucoes('concluidas')}
        />
        <KpiCard
          color="red"
          icon={XCircle}
          label="Erros"
          value={kpisExecucoes.com_erro}
          onClick={() => abrirExecucoes('com_erro')}
        />
        <KpiCard
          color="orange"
          icon={AlertTriangle}
          label="Hoje"
          value={kpisExecucoes.hoje}
          onClick={() => abrirExecucoes('todos', 'hoje')}
        />
      </div>

      {/* v2.3.0: pendências e erros — atalhos para os submenus já filtrados */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <KpiCard color="blue" icon={Clock} label="Pendências abertas" value={kpisGerais.pendencias_abertas}
          onClick={() => navigate('/tenant/workflow/pending?filtro=abertas')} />
        <KpiCard color="red" icon={AlertTriangle} label="Pendências vencidas" value={kpisGerais.pendencias_vencidas}
          onClick={() => navigate('/tenant/workflow/pending?filtro=vencidas')} />
        <KpiCard color="yellow" icon={Lock} label="Bloqueantes" value={kpisGerais.pendencias_bloqueantes}
          onClick={() => navigate('/tenant/workflow/pending?filtro=bloqueantes')} />
        <KpiCard color="orange" icon={XCircle} label="Erros abertos" value={kpisGerais.erros_abertos}
          onClick={() => navigate('/tenant/workflow/errors?status=aberto')} />
      </div>



      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : visiveis.length === 0 ? (
        <Card>
          <CardHeader className="space-y-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <WorkflowIcon className="h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Nenhum processo configurado</CardTitle>
            <CardDescription>
              Crie o primeiro processo para desenhar a esteira no construtor visual.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : modoVisual === 'list' ? (
        // v2.4.0: visualização em lista, no desenho da lista de Fluxos
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {visiveis.map((d) => (
                <WorkflowProcessListRow
                  key={d.id}
                  definicao={d}
                  selo={selo(d.status)}
                  onOpen={() => navigate(`/tenant/workflow/${d.id}`)}
                  acoes={menuAcoes(d, d.open_instance_count ?? 0)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visiveis.map((d) => {
            const abertas = d.open_instance_count ?? 0;
            const total = d.instance_count ?? 0;
            const desativado = d.status === 'archived';
            const s = selo(d.status);

            return (
              <Card
                key={d.id}
                className={cn(
                  'cursor-pointer transition-shadow hover:shadow-md',
                  desativado && 'opacity-60',
                )}
                onClick={() => navigate(`/tenant/workflow/${d.id}`)}
              >
                <CardHeader className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{d.name}</CardTitle>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* Status no padrão bolinha colorida + palavra */}
                      <StatusDotLabel tone={s.tom} label={s.texto} />

                      {/* Menu de ações do processo */}
                      {menuAcoes(d, abertas)}
                    </div>
                  </div>
                  <CardDescription className="line-clamp-2">
                    {d.description || d.root_entity_label || d.root_entity_type}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-1 text-xs text-muted-foreground">
                  <div>
                    {d.node_count} etapas · {d.edge_count} ligações
                    {d.active_version_number ? ` · v${d.active_version_number}` : ''}
                  </div>
                  <div>
                    {total} execuções
                    {abertas > 0 ? ` · ${abertas} em andamento` : ''}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}


      {/* Indicadores de gargalo (Fase 8) */}
      <WorkflowBottlenecksPanel />


      {/* Log de execuções de todos os processos (aberto pelos cards KPI) */}
      <WorkflowExecutionsDialog
        open={execucoesAbertas}
        onOpenChange={setExecucoesAbertas}
        statusInicial={execFiltroStatus}
        periodoInicial={execFiltroPeriodo}
      />

      {/* Configurações gerais do processo (edição pós-criação) */}
      {alvoConfig && (
        <WorkflowSettingsDialog
          open={!!alvoConfig}
          onOpenChange={(aberto) => !aberto && setAlvoConfig(null)}
          podeEditar={podeEditar}
          definition={{
            id: alvoConfig.id,
            name: alvoConfig.name,
            description: alvoConfig.description,
            root_entity_type: alvoConfig.root_entity_type,
            default_enforcement: alvoConfig.default_enforcement,
            workspace_ids: alvoConfig.workspace_ids,
          }}
        />
      )}

      {/* Modal de criação — mesmos campos e padrão visual do modal de edição */}
      <WorkflowDialogShell
        open={aberto}
        onOpenChange={setAberto}
        icon={GitBranch}
        title="Novo processo"
        description="Dados gerais da esteira. Depois de criar, o evento que faz o processo iniciar é escolhido no bloco Início, dentro do construtor."
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 sm:px-3"
              onClick={() => setAberto(false)}
            >
              <X className="mr-1 h-4 w-4" /> Cancelar
            </Button>
            <Button
              size="sm"
              className="h-7 px-2 sm:px-3"
              onClick={criar}
              disabled={!nome.trim() || upsertDefinition.isPending}
            >
              {upsertDefinition.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-1 h-4 w-4" />
              )}
              Criar e abrir construtor
            </Button>
          </>
        }
      >
        <WorkflowProcessFields
          nome={nome}
          onNomeChange={setNome}
          descricao={descricao}
          onDescricaoChange={setDescricao}
          entidade={entidade}
          onEntidadeChange={setEntidade}
          entidades={entidadesAtivas}
          enforcement={enforcement}
          onEnforcementChange={setEnforcement}
          workspaceIds={workspaceIds}
          onToggleWorkspace={alternarWorkspace}
          workspaces={workspaces ?? []}
        />
      </WorkflowDialogShell>

      {/* Modal de exclusão definitiva */}
      <WorkflowDialogShell
        open={!!alvoExclusao}
        onOpenChange={(v) => {
          if (!v) {
            setAlvoExclusao(null);
            setConfirmacaoNome('');
          }
        }}
        icon={Trash2}
        title="Excluir processo"
        description="Remoção definitiva do processo e de toda a sua estrutura."
        tone="destructive"
        maxWidthClass="sm:max-w-lg"
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 sm:px-3"
              onClick={() => {
                setAlvoExclusao(null);
                setConfirmacaoNome('');
              }}
            >
              <X className="mr-1 h-4 w-4" /> Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="h-7 px-2 sm:px-3"
              onClick={confirmarExclusao}
              disabled={!podeConfirmar || deleteDefinition.isPending}
            >
              {deleteDefinition.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-1 h-4 w-4" />
              )}
              Excluir definitivamente
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            O processo <span className="font-medium text-foreground">{alvoExclusao?.name}</span> será
            excluído definitivamente, junto com suas versões, etapas e ligações. Essa ação não pode ser
            desfeita.
          </p>

          {exigeDigitacao && (
            <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-destructive">
                Este processo possui {alvoExclusao?.instance_count} execução(ões) no histórico, que também
                serão apagadas. Para confirmar, digite o nome do processo abaixo.
              </p>
              <Input
                value={confirmacaoNome}
                onChange={(e) => setConfirmacaoNome(e.target.value.slice(0, 120))}
                placeholder={alvoExclusao?.name ?? ''}
              />
            </div>
          )}
        </div>
      </WorkflowDialogShell>
    </div>
  );
}
